<template>
  <v-card>
    <v-card-title class="primary accent--text">
      Summary
    </v-card-title>
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6">
          <info-list
            :list="list1()"
          />
        </v-col>
        <v-col cols="12" md="6">
          <info-list
            :list="list2()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-simple-table>
            <table>
              <thead class="accent">
                <tr>
                  <th>Description</th>
                  <th class="text-right">Quantity</th>
                  <th class="text-right">Unit Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(line, index) in lines">
                  <tr :key="`line_${index}`">
                    <td>{{ line.description }}</td>
                    <td class="text-right">{{ line.quantity }}</td>
                    <td
                      class="text-right"
                      v-money="{ amount: getUnitAmount(line), currency: invoice.currency, hideCurrency: true}"
                    ></td>
                    <td
                      v-money="{ amount: line.amount, currency: invoice.currency, hideCurrency: true}"
                    ></td>
                  </tr>
                </template>

                <tr v-if="invoice.lines.has_more && !hasLoadedAllLines">
                  <td>
                    <v-btn
                      text
                      rounded
                      small
                      color="primary"
                      @click="loadAllItems"
                      :loading="isLoading"
                    >
                      View all {{ invoice.lines.total_count }} items
                    </v-btn>
                  </td>
                  <td></td>
                  <td></td>
                  <td></td>
                </tr>

                <!-- SubTotal Row -->
                <tr>
                  <td></td>
                  <td></td>
                  <td class="grey--text text-right">Subtotal</td>
                  <td
                    v-money="{ amount: invoice.subtotal, currency: invoice.currency, hideCurrency: true}"></td>
                </tr>

                <!-- Coupon Row -->
                <tr v-if="hasDiscount()">
                  <td></td>
                  <td></td>
                  <td class="grey--text text-right">
                    <span class="grey--text">{{ _get(invoice, 'discount.coupon.name', '') }}</span><br>
                    <span class="caption grey--text">{{ couponTerms() }}</span>
                  </td>
                  <td>
                    {{ getDiscountTotal() }}
                  </td>
                </tr>

                <!-- Taxes Row -->
                <template v-for="(tax, taxIndex) in getTaxes() ">
                  <tr>
                    <td></td>
                    <td></td>
                    <td class="grey--text text-right">
                      {{ getTaxDisplay(tax.tax_rate) }}
                    </td>
                    <td>
                      <div
                        v-money="{ amount: tax.amount, currency: invoice.currency, hideCurrency: true}">
                        {{tax.amount}}
                      </div>
                    </td>
                  </tr>
                </template>

                <!-- Total Row -->
                <tr>
                  <td></td>
                  <td></td>
                  <td class="grey--text text-right">
                    Total
                  </td>
                  <td
                    v-money="{ amount: invoice.total, currency: invoice.currency, hideCurrency: true}">

                  </td>
                </tr>

                <!-- Balance Row -->
                <tr v-if="hasBalanceApplied()">
                  <td></td>
                  <td></td>
                  <td class="grey--text text-right">
                    Applied balance
                  </td>
                  <td
                    v-money="{ amount: invoice.starting_balance, currency: invoice.currency, hideCurrency: true}">

                  </td>
                </tr>

                <!-- Amount paid row -->
                <tr>
                  <td></td>
                  <td></td>
                  <td class="grey--text text-right">
                    Amount Paid
                  </td>
                  <td
                    v-money="{ amount: invoice.amount_paid > 0 ? invoice.amount_paid * -1 : invoice.amount_paid, currency: invoice.currency, hideCurrency: true}">

                  </td>
                </tr>

                <!-- Amount due Row -->
                <tr>
                  <td></td>
                  <td></td>
                  <td class=" text-right">
                    Amount Due
                  </td>
                  <td
                    v-money="{ amount: invoice.amount_remaining, currency: invoice.currency, hideCurrency: true}">

                  </td>
                </tr>

              </tbody>

            </table>
          </v-simple-table>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
import InfoList from "../Common/InfoList"
import { get, isNumber } from "lodash"
import { discountTotal, formatDate } from "../../services/util.service"
import { readableCouponTerms } from "../../services/stripe.service"
import { mapActions } from 'vuex'

export default {
  components: { InfoList },
  props: {
    invoice: {
      type: Object,
      default: () => ({})
    }
  },
  watch: {
    'invoice'() {
      this.lines = get(this.invoice, 'lines.data', []) || []
    }
  },
  data() {
    return {
      lines: [],
      isLoading: false,
      hasLoadedAllLines: false
    }
  },
  mounted() {
     this.lines = get(this.invoice, 'lines.data', []) || []
  },
  methods: {
    ...mapActions(['listInvoiceItems']),
    getUnitAmount(line) {
      const { quantity = 1, amount = 0 } = line

      // Protect divide by 0
      return quantity < 1 ? 0 : (amount/quantity)
    },
    loadAllItems() {

      if (!this.invoice.id) {
        return
      }

      let lineItems = []

      const loadLineItems = (startingAfter) => {
        const listItemsPayload = {
          query: {
            limit: 100,
            invoice: this.invoice.id
          }
        }

        if (startingAfter) {
          listItemsPayload.query.starting_after = startingAfter
        }

        this.listInvoiceItems(listItemsPayload)
          .then(res => {
            const { data, has_more } = res

            lineItems = [ ...lineItems, ...data ]

            if (has_more) {
              const lastId = get(data, `[${data.length - 1}].id`)
              loadLineItems(lastId)
            } else {
              this.lines =  [ ...lineItems ]
              this.isLoading = false
              this.hasLoadedAllLines = true
            }
          })
      }

      this.isLoading = true
      loadLineItems()
    },
    couponTerms() {
      const coupon = get(this.invoice, 'discount.coupon')
      return coupon ? readableCouponTerms(coupon) : ''
    },
    getDiscountTotal() {
      const payload = {
        subtotal: this.invoice.subtotal,
        currency: this.invoice.currency,
        coupon: get(this.invoice, 'discount.coupon', {})
      }

      return discountTotal(payload)
    },
    _get: (val, path, def) => get(val, path, def),
    hasDiscount() {
      const coupon = get(this.invoice, 'discount.coupon')

      return !!coupon && (parseInt(coupon.amount_off, 10) > 0 || parseInt(coupon.percent_off, 10) > 0 )
    },
    hasBalanceApplied() {
      return isNumber(this.invoice.starting_balance)
       && (this.invoice.starting_balance > 0 || this.invoice.starting_balance < 0)
    },
    getAmountPaid() {

    },
    getAmountDue() {

    },
    getTaxDisplay(tax) {
      const percent = `${tax.percentage}% ${tax.inclusive ? 'incl.' : ''}`.trim()
      return `${tax.display_name} - ${tax.jurisdiction} (${percent})`
    },
    getTaxes() {
      const { total_tax_amounts } = this.invoice
      return total_tax_amounts
    },
    list1() {
      return [
        {
          label: 'Billed to',
          value: get(this.invoice, 'customer_email', '')
        },
        {
          label: 'Name',
          value: get(this.invoice, 'customer_name', '')
        },
        {
          label: 'Address',
          value: [
            get(this.invoice, 'customer_address.line1'),
            get(this.invoice, 'customer_address.line2'),
            `${get(this.invoice, 'customer_address.city')},
                ${get(this.invoice, 'customer_address.state', '')}
                ${get(this.invoice, 'customer_address.postal_code', '')}`,
            get(this.invoice, 'customer_address.country'),
          ]
        },
        {
          label: 'Phone number',
          value: get(this.invoice, 'customer_phone', '')
        },
        {
          label: 'Currency',
          value: get(this.invoice, 'currency', '')
        },
      ]
    },
    list2() {
      return [
        {
          label: 'Invoice number',
          value: get(this.invoice, 'number', '')
        },
        {
          label: 'Due date',
          value: formatDate(get(this.invoice, 'due_date', ''))
        },
        {
          label: 'Billing method',
          value: get(this.invoice, 'collection_method', '')
        },
        {
          label: 'Memo',
          value: get(this.invoice, 'description', '')
        },
      ]
    }
  }
}
</script>
